<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import store from '@/store'
import { ALERTS_URL, download, getDatedFilename } from '@/main'
import {
  alertsDisplaySettingsPreference,
  alertsOptionsPreference,
  defaultAlertsDisplaySettings,
} from '@/components/alerts/alertsPreferences'
import ReportControls from '@/components/ReportControls.vue'
import AlertsTable from '@/components/alerts/AlertsTable.vue'
import SaveDialog from '@/components/dialogs/SaveDialog.vue'
import ACLDialog from '@/components/auth/ACLDialog'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import OptionsDropdown from '@/components/OptionsDropdown.vue'
import TransferMenu from '@/components/TransferMenu.vue'
import TableControls from '@/components/report-page-layouts/TableControls.vue'
import TableCardLayout from '@/components/report-page-layouts/TableCardLayout.vue'
import { showErrorToastWithTimeout, showErrorToast } from '@/utils/toast'
import NestedCategorySelect from '@/components/categorized-nested-select/NestedCategorySelect.vue'
import { groupBy, cloneDeep } from 'lodash-es'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const proxy = getCurrentInstance().proxy

const status = ref(null)
const alerts = ref([])
const options = alertsOptionsPreference
const alertsListLoading = ref(true)
const report = ref(null)
const search = ref(null)
const selectedStructures = ref([])
const maxKey = ref(0)

const getCheckedStructures = computed(() => store.getters.getCheckedStructures)
const isAdmin = computed(() => store.getters.isAdmin)
const isAuthenticated = computed(() => store.getters.isAuthenticated)
const alertsResult = computed(() => store.getters.alertsResult)
const getStructures = computed(() => store.getters.getStructures)
const stdizerResult = computed(() => store.getters.stdizerResult)

const isMobile = computed(() => proxy.$vuetify.breakpoint.xs)

const createDisplaySetting = (key) =>
  computed({
    get: () => alertsDisplaySettingsPreference.value[key],
    set: (value) => {
      alertsDisplaySettingsPreference.value = {
        ...alertsDisplaySettingsPreference.value,
        [key]: Boolean(value),
      }
    },
  })

const collapse = createDisplaySetting('collapse')
const showDetails = createDisplaySetting('showDetails')
const showImages = createDisplaySetting('showImages')
const showEmpty = createDisplaySetting('showEmpty')

const areDisplayOptionsChanged = computed(() =>
  Object.entries(defaultAlertsDisplaySettings).some(
    ([key, defaultValue]) => alertsDisplaySettingsPreference.value[key] !== defaultValue,
  ),
)

const reportId = computed(() => proxy.$route.params.id)
const readyToCalculate = computed(() => !!getCheckedStructures.value?.length && !!options.value.alerts?.length)

const reportControlsStatus = computed(() => {
  if (status.value === 'PENDING') return 'loading'
  if (report.value) return readyToCalculate.value ? 'stale' : 'fresh'
  return readyToCalculate.value ? 'ready' : 'invalid'
})

const getCategorizedAlerts = computed(() => {
  if (!alerts.value || alerts.value.length === 0) return []
  const grouped = groupBy(alerts.value, 'category')
  return Object.entries(grouped).map(([category, items]) => ({
    id: category,
    name: category,
    children: items.map((alert) => ({
      ...alert,
      id: alert?.name,
      name: alert?.text,
      description: alert?.description,
    })),
  }))
})

const alertsExportItems = computed(() => [
  { title: 'Export to SDF', icon: 'mdi-file-download-outline', onClick: () => exportFile('SDF') },
  { title: 'Export to CSV', icon: 'mdi-file-delimited-outline', onClick: () => exportFile('CSV') },
  { title: 'Export to XLSX', icon: 'mdi-file-excel-outline', onClick: () => exportFile('XLSX') },
  { title: 'Export to SMI', icon: 'mdi-file-document-outline', onClick: () => exportFile('SMI') },
])

const selectedStructuresAsSet = computed(() => new Set(selectedStructures.value.map((structure) => structure.smiles)))
const getStructuresAsMap = computed(
  () => new Map(getStructures.value.map((structure) => [structure?.chemical?.inchiKey, structure])),
)
const recordsForTransfer = computed(() => cloneDeep(selectedStructures.value))

const exportFile = (format) => {
  status.value = 'EXPORTING'
  const formatLowerCased = format.toLowerCase()
  const url = `${ALERTS_URL}/export`
  const rpt = cloneDeep(report.value)
  rpt.alerts = rpt?.alerts?.filter((c) =>
    selectedStructuresAsSet.value?.size > 0 ? selectedStructuresAsSet.value.has(c.chemical.smiles) : true,
  )
  rpt.alerts.forEach((alert) => {
    alert.chemical.additionalProps = getStructuresAsMap.value.get(alert?.chemical?.inchiKey)?.chemical.additionalProps
  })

  proxy.$axios
    .post(
      url,
      {
        alertResult: rpt,
        format: formatLowerCased.toUpperCase(),
        collapse: collapse.value,
        showDetails: showDetails.value,
        showImages: showImages.value,
        showAlertsOnly: !showEmpty.value,
      },
      { responseType: 'arraybuffer' },
    )
    .then((response) => {
      const filename = getDatedFilename('alerts', formatLowerCased)
      download(filename, response.data, formatLowerCased)
      status.value = 'DONE'
    })
    .catch((err) => {
      console.error('Error during export:', err)
      status.value = 'ERROR'
      showErrorToastWithTimeout(err, 'Error during export')
    })
}

const onReset = () => {
  if (reportId.value) {
    proxy.$axios.delete(`${ALERTS_URL}reports/${report.value.id}`).then(() => {
      if (report.value.id === stdizerResult.value.id) store.commit('resetStdizerResult')
    })
  } else {
    store.commit('resetAlertsResult')
  }
}

const onProtocolCreated = (newReport) => {
  report.value = newReport
  proxy.$router.push(`/alerts/reports/${encodeURIComponent(newReport.id)}`)
}

const onProtocolUpdated = (newReport) => (report.value = newReport)
const onAclUpdated = (acl) => {
  if (report.value) report.value.acl = acl
}

const onAlert = (chemicals) => {
  store.commit('SET_VIEW_STRUCTURES', false)
  status.value = 'PENDING'

  proxy.$axios
    .post(`${ALERTS_URL}`, {
      chemicals: Array.isArray(chemicals) ? chemicals : getCheckedStructures.value,
      options: options.value,
    })
    .then((response) => {
      status.value = 'DONE'
      store.commit('setAlertsResult', response.data)
    })
    .catch((err) => {
      status.value = 'ERROR'
      showErrorToast(err)
    })
}

// const onFilesUploaded = (result) => store.commit('setAlertsResult', result)

const getAlerts = () => {
  alertsListLoading.value = true
  return proxy.$axios
    .get(`${ALERTS_URL}/alerts`)
    .then((response) => {
      alerts.value = response.data
      const selectedAlerts = Array.isArray(options.value.alerts) ? options.value.alerts : []
      options.value = {
        ...options.value,
        alerts: selectedAlerts.filter((a) => alerts.value.map((o) => o.name).includes(a)).sort(),
      }
    })
    .catch((err) => {
      showErrorToast(err, { title: 'Unable to fetch alerts' })
    })
    .finally(() => (alertsListLoading.value = false))
}

watch(
  alertsResult,
  (newValue) => {
    report.value = cloneDeep(newValue)
    maxKey.value = 0
    report.value?.alerts?.forEach((alert) => (alert.ordinal = maxKey.value++))
  },
  { immediate: true },
)

const maybeOpenChemCart = () => {
  if (!report.value && !getCheckedStructures.value?.length) store.commit('SET_VIEW_STRUCTURES', true)
}

onMounted(() => {
  maybeOpenChemCart()
  getAlerts()
})
</script>

<script>
import AuthBase from '@/components/auth/AuthBase.vue'

export default { mixins: [AuthBase] }
</script>

<template>
  <div class="d-flex flex-column h-100">
    <TableCardLayout>
      <template #table-controls>
        <TableControls :loading="status === 'PENDING'">
          <template #left>
            <NestedCategorySelect
              v-model="options.alerts"
              :tree-items="getCategorizedAlerts"
              :loading="alertsListLoading"
              :activator-label="isMobile ? 'Alerts' : 'Select alerts'"
              expanded-state-storage-key="alerts.select"
              :v-btn-props="{ class: 'flat-button border' }"
              allow-initial-empty-selection
            />

            <ReportControls
              :status="reportControlsStatus"
              calculate-id="alerts-generate-button"
              calculate-tooltip="Generate alerts for chemicals in ChemCart"
              calculate-aria-label="Generate alerts for chemicals in ChemCart"
              @calculate="onAlert"
              @clear="onReset"
            />

            <v-btn-toggle
              class="ml-3"
              dense
            >
              <v-btn
                v-if="isAuthenticated"
                id="alerts-std-protocols-button"
                class="no-link-under"
                to="/alerts/reports"
                aria-label="Standardization protocols"
              >
                <v-icon>mdi-table-heart</v-icon>
              </v-btn>

              <v-tooltip
                top
                v-bind="defaultVTooltipProps"
                activator="#alerts-std-protocols-button"
              >
                Standardization protocols
              </v-tooltip>

              <v-btn
                v-if="isAdmin"
                id="alerts-workflows-button"
                class="no-link-under"
                to="/alerts/groups"
                aria-label="Workflows definitions"
              >
                <v-icon>mdi-database</v-icon>
              </v-btn>
              <v-tooltip
                top
                v-bind="defaultVTooltipProps"
                activator="#alerts-workflows-button"
              >
                Workflows definitions
              </v-tooltip>
            </v-btn-toggle>
          </template>

          <template #right>
            <template v-if="report">
              <v-tooltip
                top
                dense
                v-bind="defaultVTooltipProps"
                :open-on-focus="false"
                :open-on-click="false"
              >
                <template #activator="{ on }">
                  <v-text-field
                    v-on="on"
                    v-model="search"
                    hide-details
                    dense
                    style="max-width: 10rem"
                  >
                    <template #label>
                      <v-icon
                        small
                        left
                      >
                        mdi-magnify
                      </v-icon>
                      <span class="text-muted font-weight-light small-caption">Search</span>
                    </template>
                    <template #append>
                      <v-icon
                        v-if="search"
                        small
                        class="cursor-pointer"
                        @click="search = ''"
                      >
                        mdi-close-circle
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                Search in any field
              </v-tooltip>

              <v-menu
                offset-y
                :close-on-content-click="false"
                max-width="20rem"
              >
                <template #activator="{ on: menuOn, attrs: menuAttrs }">
                  <v-tooltip
                    top
                    v-bind="defaultVTooltipProps"
                  >
                    <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                      <v-btn
                        v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        aria-label="Show display options"
                        class="flat-button border"
                      >
                        <v-badge
                          :value="!!areDisplayOptionsChanged"
                          dot
                          offset-x="0"
                          offset-y="0"
                        >
                          <v-icon>mdi-tune-variant</v-icon>
                        </v-badge>
                      </v-btn>
                    </template>
                    <span>Show display options</span>
                  </v-tooltip>
                </template>

                <v-card>
                  <v-card-subtitle>Display options</v-card-subtitle>
                  <v-divider class="mx-4 my-0" />
                  <v-card-text class="pt-1">
                    <div
                      class="d-flex flex-column"
                      style="gap: 1rem"
                    >
                      <OptionsDropdown
                        v-if="isMobile"
                        :items="alertsExportItems"
                        :loading="status === 'EXPORTING'"
                        title="Export to SDF, Excel, Word or PDF"
                        :disabled="!report?.alerts?.length"
                        :label="`Export to file (${selectedStructures?.length})`"
                        show-label
                      />

                      <div class="d-flex flex-column">
                        <v-switch
                          v-model="showEmpty"
                          label="Show Empty"
                          title="Show chemicals with no alerts"
                          dense
                          hide-details
                        />
                        <v-switch
                          v-model="collapse"
                          label="Collapse"
                          title="Collapse all alerts into a single one"
                          dense
                          hide-details
                        />
                        <v-switch
                          v-model="showDetails"
                          label="Details"
                          title="Show/hide additional information"
                          dense
                          hide-details
                        />
                        <v-switch
                          v-model="showImages"
                          label="Show Structure"
                          title="Show/Hide chemical structure"
                          dense
                          hide-details
                        />
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>

              <v-btn-toggle
                dense
                v-if="isAuthenticated || (report?.id && canACL(report)) || canDelete(report)"
              >
                <SaveDialog
                  v-if="isAuthenticated"
                  :api-url="`${ALERTS_URL}/reports`"
                  :button-title="report.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline'"
                  :item="report"
                  :title="report.id ? 'Edit Protocol' : 'Save Protocol'"
                  @itemCreated="onProtocolCreated"
                  @itemUpdated="onProtocolUpdated"
                />

                <ACLDialog
                  v-if="report?.id && canACL(report)"
                  :api-url="ALERTS_URL"
                  :item="report"
                  button-title="mdi-account-key"
                  title="Access Control"
                  type="reports"
                  @aclUpdated="onAclUpdated"
                />

                <PromptDialog
                  v-if="canDelete(report)"
                  :text="report.id ? 'Delete this Protocol' : 'Do you want to clear Protocol?'"
                  :title="report.id ? 'Delete Protocol' : 'Clear Protocol'"
                  button-title="mdi-delete-outline"
                  @confirmed="onReset"
                />
              </v-btn-toggle>

              <OptionsDropdown
                v-if="!isMobile"
                :items="alertsExportItems"
                :loading="status === 'EXPORTING'"
                title="Export to SDF, Excel, Word or PDF"
                :disabled="!report?.alerts?.length"
                :label="`${selectedStructures?.length}`"
                :show-label="!!selectedStructures?.length"
              />

              <TransferMenu
                :records="recordsForTransfer"
                hide-add-button
                only-icon
              />
            </template>
          </template>
        </TableControls>
      </template>

      <AlertsTable
        v-if="report?.alerts?.length"
        :loading="status === 'PENDING'"
        :report="report"
        :collapse="collapse"
        :show-details="showDetails"
        :show-images="showImages"
        :show-alerts-only="!showEmpty"
        :selected-structures="selectedStructures"
        :search="search"
        @update:selectedStructures="(val) => (selectedStructures = val)"
      />
    </TableCardLayout>
  </div>
</template>

<style scoped>
.predict-error {
  padding: 0;
  margin-left: 10px;
}

.predict-error div {
  padding: 6px;
}
</style>
